import { useContext } from 'react';

import CreateEditSchemaModalContext from '@atom/components/common/createEditSchemaModal/CreateEditSchemaModalContext';
import { Icon, RadioGroup, Switch, Tooltip } from '@atom/mui';
import { StartEndReadingOption, StockBasedOption } from '@atom/types/schema';

import LabelWithDescription from './LabelWithDescription';

import classes from './createEditSchemaModal.module.css';

const { RadioButton } = RadioGroup;

const styles = {
  helpIcon: {
    fontSize: '1.125rem',
  },
};

const TOOLTIP =
  'Inventory can be the target of the work, and it can also be used to perform work.';

const CostTrackingTab = () => {
  const {
    isMaterial,
    setIsMaterial,
    stockBasedOption,
    setStockBasedOption,
    startEndReadingOption,
    setStartEndReadingOption,
  } = useContext(CreateEditSchemaModalContext);

  const stockEnabled =
    stockBasedOption === StockBasedOption.STOCK_BASED ||
    stockBasedOption === StockBasedOption.STOCK_BASED_OPTIONAL;
  const startEndEnabled =
    startEndReadingOption === StartEndReadingOption.START_END_READING ||
    startEndReadingOption === StartEndReadingOption.START_END_READING_OPTIONAL;

  const handleStockToggle = (checked: boolean) => {
    if (checked) {
      setStockBasedOption(StockBasedOption.STOCK_BASED);
      setStartEndReadingOption(StartEndReadingOption.NONE);
    } else {
      setStockBasedOption(StockBasedOption.NONE);
    }
  };

  const handleStartEndToggle = (checked: boolean) => {
    if (checked) {
      setStartEndReadingOption(StartEndReadingOption.START_END_READING);
      setStockBasedOption(StockBasedOption.NONE);
    } else {
      setStartEndReadingOption(StartEndReadingOption.NONE);
    }
  };

  const stockDescription = startEndEnabled ? (
    <>
      Not available when <strong>Start and End Readings</strong> are enabled.
    </>
  ) : (
    'Usage and cost tracked through quantity used and inventory level available.'
  );

  const startEndDescription = stockEnabled ? (
    <>
      Not available when <strong>Stock-based Inventory</strong> is enabled.
    </>
  ) : (
    'Usage and cost tracked through start and end readings.'
  );

  return (
    <div className={classes.tabContainer}>
      <div className={classes.tabTitle}>Cost Tracking</div>
      <Switch
        checked={isMaterial}
        onChange={(_, toggled) => setIsMaterial(toggled)}
        label={
          <span className={classes.labelWithTooltip}>
            Track cost and usage during work
            <Tooltip title={TOOLTIP}>
              <span className={classes.tooltipTarget}>
                <Icon style={styles.helpIcon}>help</Icon>
              </span>
            </Tooltip>
          </span>
        }
      />
      {isMaterial && (
        <>
          <Switch
            checked={stockEnabled}
            disabled={startEndEnabled}
            onChange={(_, toggled) => handleStockToggle(toggled)}
            label={
              <LabelWithDescription
                title="Stock-based Inventory"
                description={stockDescription}
              />
            }
          />
          {stockEnabled && (
            <div className={classes.radioGroupIndent}>
              <RadioGroup
                value={stockBasedOption}
                onChange={(_, value) =>
                  setStockBasedOption(value as StockBasedOption)
                }
                sx={{ gap: '1rem' }}
              >
                <RadioButton
                  value={StockBasedOption.STOCK_BASED}
                  label={
                    <LabelWithDescription
                      title="Default"
                      description="Set an available inventory level and track usage for all inventory of this type."
                    />
                  }
                />
                <RadioButton
                  value={StockBasedOption.STOCK_BASED_OPTIONAL}
                  label={
                    <LabelWithDescription
                      title="Optional"
                      description="Set an available inventory level and track usage at the individual inventory item level."
                    />
                  }
                />
              </RadioGroup>
            </div>
          )}
          <Switch
            checked={startEndEnabled}
            disabled={stockEnabled}
            onChange={(_, toggled) => handleStartEndToggle(toggled)}
            label={
              <LabelWithDescription
                title="Start and End Readings"
                description={startEndDescription}
              />
            }
          />
          {startEndEnabled && (
            <div className={classes.radioGroupIndent}>
              <RadioGroup
                value={startEndReadingOption}
                onChange={(_, value) =>
                  setStartEndReadingOption(value as StartEndReadingOption)
                }
                sx={{ gap: '1rem' }}
              >
                <RadioButton
                  value={StartEndReadingOption.START_END_READING}
                  label={
                    <LabelWithDescription
                      title="Default"
                      description="Enable readings for all inventory items of this type."
                    />
                  }
                />
                <RadioButton
                  value={StartEndReadingOption.START_END_READING_OPTIONAL}
                  label={
                    <LabelWithDescription
                      title="Optional"
                      description="Enable readings at the individual inventory item level."
                    />
                  }
                />
              </RadioGroup>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CostTrackingTab;
